import {
  ApiException,
  ApplePayMerchantSessionClientRequest,
  IErrorResponse,
  PaymentConfiguration,
} from "@dining/api-client";
import { StateSlice } from "./app";
import {
  appInsightsEvents,
  createMobilePayment,
  createPaymentRequest,
  supportsApplePay,
  trackEvent,
} from "../..";
import { applePayConfig } from "../../../configs";
import { FeatureName, FeatureNameType } from "../../featureFlags";

export type PaymentsStateProps = {
  /**
   * Gets the payment configuration (fetched from the API)
   */
  paymentConfig?: PaymentConfiguration;
  /**
   * Gets the payment configuration error (if any)
   */
  paymentConfigError?: Error | IErrorResponse;
  /**
   * Gets whether Apple Pay is enabled
   */
  applePayEnabled: boolean;
};

/**
 * State slice for Payments
 */
export type PaymentsState = PaymentsStateProps & {
  /**
   * Sets the payment configuration
   * @param paymentConfig payment configuration
   * @returns void
   */
  setPaymentConfig: (paymentConfig: PaymentConfiguration) => void;
  /**
   * Fetches the payment configuration from the API.
   * @returns Promise<void>
   */
  fetchPaymentConfig: () => Promise<void>;

  /**
   * Sets the payment configuration error
   * @param error error
   * @returns void
   */
  setPaymentConfigError: (error: Error | IErrorResponse) => void;

  /**
   * Sets whether Apple Pay is enabled
   * @param applePayEnabled whether Apple Pay is enabled
   * @returns void
   */
  setApplePayEnabled: (applePayEnabled: boolean) => void;

  /**
   * Checks whether the current user has access to a given feature
   * by calling the HasAccessToFeatureAsync API.
   * @param featureName the feature to check access for
   * @returns Promise<boolean>
   */
  hasAccessToFeatureAsync: (featureName: FeatureNameType) => Promise<boolean>;

  payWithApplePay: () => Promise<void>;
};

/**
 * Default payments state
 */
export const defaultPaymentsProps = {
  applePayEnabled: false,
};

export const createPaymentsState: (
  props?: Partial<PaymentsStateProps>,
) => StateSlice<PaymentsState> = (props) => (set, get) => ({
  ...defaultPaymentsProps,
  ...props,
  hasAccessToFeatureAsync: async (featureName: FeatureNameType): Promise<boolean> => {
    const { client, appInsights, context } = get();
    try {
      const hasAccess = await client?.hasAccessToFeature(featureName);
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.Payment.ApplePay.FeatureAccessCheck,
        context,
        {
          featureName,
          hasAccess: !!hasAccess,
        },
      );
      return !!hasAccess;
    } catch (error) {
      appInsights?.trackException(
        { exception: error as Error },
        {
          name: "hasAccessToFeatureAsync",
          featureName,
        },
      );
      return false;
    }
  },
  fetchPaymentConfig: async () => {
    const {
      client,
      setPaymentConfigError,
      setPaymentConfig,
      setApplePayEnabled,
      hasAccessToFeatureAsync,
    } = get();
    try {
      const hasApplePayAccess = await hasAccessToFeatureAsync(FeatureName.ApplePay);
      const response = await client?.getPaymentConfig();
      if (response) {
        setPaymentConfig(response);
        if (
          hasApplePayAccess &&
          response.supportedMethods?.find((m) => m.method === "apple-pay") &&
          supportsApplePay()
        ) {
          setApplePayEnabled(true);
        } else {
          setApplePayEnabled(false);
        }
      }
    } catch (error) {
      if (ApiException.isApiException(error)) {
        setPaymentConfigError(error);
      } else {
        setPaymentConfigError(error as Error);
      }
    }
  },
  setPaymentConfig: (paymentConfig: PaymentConfiguration) => {
    set({ paymentConfig }, false, { type: "setPaymentConfig", paymentConfig });
  },
  setPaymentConfigError: (paymentConfigError: Error | IErrorResponse) => {
    set({ paymentConfigError }, false, { type: "setPaymentConfigError", paymentConfigError });
  },
  setApplePayEnabled: (applePayEnabled: boolean) => {
    set({ applePayEnabled }, false, { type: "setApplePayEnabled", applePayEnabled });
  },
  payWithApplePay: async () => {
    const { appInsights, cart, client, context, orders, paymentConfig } = get();
    const { setSelected, setError } = orders;
    try {
      const canMakePayments = ApplePaySession.canMakePayments();
      if (!canMakePayments) {
        throw new Error("Apple Pay is not supported on this device");
      }
      const paymentRequest = createPaymentRequest(cart.value!, paymentConfig!);
      // `14` is the version of the Apple Pay API
      const session = new ApplePaySession(14, paymentRequest);
      // Validate the merchant by obtaining a merchant session from the server
      session.onvalidatemerchant = async (event: ApplePayJS.ApplePayValidateMerchantEvent) => {
        try {
          const sessionRequest = ApplePayMerchantSessionClientRequest.fromJS({
            validationUrl: applePayConfig.validationUrl || event.validationURL,
            merchantId: paymentConfig?.applePayMerchantIdentifier,
            initiativeContext: applePayConfig.initiativeContext,
          });
          const sessionResponse = await client?.getApplePayMerchantSession(
            undefined,
            sessionRequest,
          );

          trackEvent(
            appInsights,
            appInsightsEvents.Usage.Actions.Payment.ApplePay.MerchantSessionRequestSuccess,
            context,
            {
              method: "payWithApplePay.session.onvalidatemerchant",
              cartId: cart.value?.id,
              ...sessionRequest,
            },
          );
          session.completeMerchantValidation(sessionResponse?.toJSON());
        } catch (error) {
          appInsights.trackException(
            { exception: error as Error },
            {
              name: appInsightsEvents.Usage.Actions.Payment.ApplePay.MerchantSessionRequestFailure,
              method: "payWithApplePay.session.onvalidatemerchant",
              cartId: cart.value?.id,
            },
          );
          throw error;
        }
      };
      // Handle payment authorization
      session.onpaymentauthorized = async (event: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
        try {
          const payment = createMobilePayment(event, context);
          const order = await client?.payWithMobile(undefined, payment);
          setSelected(order);
          trackEvent(
            appInsights,
            appInsightsEvents.Usage.Actions.Payment.ApplePay.PaymentSuccess,
            context,
            {
              method: "payWithApplePay.session.onpaymentauthorized",
              cartId: cart.value?.id,
              orderId: order?.id,
            },
          );
          session.completePayment(ApplePaySession.STATUS_SUCCESS);
        } catch (error) {
          const result: ApplePayJS.ApplePayPaymentAuthorizationResult = {
            status: ApplePaySession.STATUS_FAILURE,
            errors: new Array<ApplePayError>(),
          };

          appInsights.trackException(
            { exception: error as Error },
            {
              name: appInsightsEvents.Usage.Actions.Payment.ApplePay.PaymentFailure,
              method: "payWithApplePay.session.onpaymentauthorized",
              cartId: cart.value?.id,
            },
          );
          setError(error as Error);
          session.completePayment(result);
          throw error;
        }
      };
      session.begin();
    } catch (error) {
      // handle case where canMakePayment is false
      appInsights.trackException(
        { exception: error as Error },
        {
          name: appInsightsEvents.Usage.Actions.Payment.ApplePay.PaymentFailure,
          method: "payWithApplePay",
          cartId: cart.value?.id,
          reason: "payment failed",
        },
      );
      setError(new Error("Payment through apple pay failed"));
    }
  },
});
